import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

// Brand colors
const COLORS = {
  navy: '#101B3A',
  red: '#C7163A',
  white: '#FFFFFF'
};

// Star Rating Input
function StarRatingInput({ value, onChange }) {
  const [hover, setHover] = useState(null);
  
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(null)}
          onClick={() => onChange(star === value ? star - 0.5 : star)}
          className="focus:outline-none"
        >
          <svg 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill={(hover || value || 0) >= star ? COLORS.red : '#E5E5E5'}
          >
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
          </svg>
        </button>
      ))}
      <span className="ml-2 text-sm text-gray-500">{value || 0}/5</span>
      {value && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="ml-2 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded"
        >
          Reset
        </button>
      )}
    </div>
  );
}

// File Upload Component
function FileUpload({ label, currentUrl, onUpload, accept = ".png,.jpg,.jpeg,.pdf" }) {
  const [uploading, setUploading] = useState(false);
  
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await api.post('/voting-guide/manage/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onUpload(res.data.url);
      toast.success(`${label} uploaded successfully`);
    } catch (e) {
      console.error(e);
      toast.error(`Failed to upload ${label}`);
    }
    setUploading(false);
  };
  
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      {currentUrl && (
        <div className="flex items-center gap-2 p-2 bg-green-50 rounded border border-green-200">
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <a href={currentUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate max-w-[150px]">
            {currentUrl.split('/').pop()}
          </a>
          <button 
            type="button"
            onClick={() => onUpload(null)} 
            className="ml-auto px-2 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-600 rounded"
          >
            Delete
          </button>
        </div>
      )}
      <input
        type="file"
        accept={accept}
        onChange={handleUpload}
        disabled={uploading}
        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 disabled:opacity-50"
      />
      {uploading && <div className="text-sm text-gray-500">Uploading...</div>}
    </div>
  );
}

// Electorate Edit Modal
function ElectorateEditModal({ electorate, onClose, onSave }) {
  const [form, setForm] = useState({
    mp_name: electorate.custom_mp_name || electorate.base_mp_name || '',
    mp_party: electorate.custom_mp_party || electorate.base_mp_party || '',
    mp_photo_url: electorate.mp_photo_url || '',
    mp_description: electorate.mp_description || '',
    star_rating: electorate.star_rating || null,
    voting_report_png_url: electorate.voting_report_png_url || '',
    voting_report_pdf_url: electorate.voting_report_pdf_url || '',
    htv_card_png_url: electorate.htv_card_png_url || '',
    htv_card_pdf_url: electorate.htv_card_pdf_url || ''
  });
  const [saving, setSaving] = useState(false);
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/voting-guide/manage/electorate/${encodeURIComponent(electorate.electorate_name)}`, form);
      toast.success(`${electorate.electorate_name} updated successfully`);
      onSave();
      onClose();
    } catch (e) {
      console.error(e);
      toast.error('Failed to save changes');
    }
    setSaving(false);
  };
  
  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b sticky top-0 bg-white">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold" style={{ color: COLORS.navy }}>
              Edit {electorate.electorate_name}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Base MP: {electorate.base_mp_name || 'Unknown'} ({electorate.base_mp_party || 'Unknown'})
          </p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* MP Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">MP Name</label>
              <Input
                value={form.mp_name}
                onChange={(e) => setForm({...form, mp_name: e.target.value})}
                placeholder="MP Name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Party</label>
              <Input
                value={form.mp_party}
                onChange={(e) => setForm({...form, mp_party: e.target.value})}
                placeholder="Party"
              />
            </div>
          </div>
          
          {/* Description */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Short Description</label>
            <Textarea
              value={form.mp_description}
              onChange={(e) => setForm({...form, mp_description: e.target.value})}
              placeholder="Brief description for the widget..."
              rows={3}
            />
          </div>
          
          {/* Star Rating */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">Star Rating</label>
            <StarRatingInput
              value={form.star_rating}
              onChange={(val) => setForm({...form, star_rating: val})}
            />
          </div>
          
          {/* MP Photo */}
          <FileUpload
            label="MP Photo"
            currentUrl={form.mp_photo_url}
            onUpload={(url) => setForm({...form, mp_photo_url: url})}
            accept=".png,.jpg,.jpeg,.webp"
          />
          
          {/* Transparency Report */}
          <div className="border-t pt-4">
            <h3 className="font-semibold mb-4" style={{ color: COLORS.navy }}>Transparency Report</h3>
            <div className="grid grid-cols-2 gap-4">
              <FileUpload
                label="PNG Version"
                currentUrl={form.voting_report_png_url}
                onUpload={(url) => setForm({...form, voting_report_png_url: url})}
                accept=".png"
              />
              <FileUpload
                label="PDF Version"
                currentUrl={form.voting_report_pdf_url}
                onUpload={(url) => setForm({...form, voting_report_pdf_url: url})}
                accept=".pdf"
              />
            </div>
          </div>
          
          {/* HTV Card */}
          <div className="border-t pt-4">
            <h3 className="font-semibold mb-4" style={{ color: COLORS.navy }}>How to Vote Card</h3>
            <div className="grid grid-cols-2 gap-4">
              <FileUpload
                label="PNG Version"
                currentUrl={form.htv_card_png_url}
                onUpload={(url) => setForm({...form, htv_card_png_url: url})}
                accept=".png"
              />
              <FileUpload
                label="PDF Version"
                currentUrl={form.htv_card_pdf_url}
                onUpload={(url) => setForm({...form, htv_card_pdf_url: url})}
                accept=".pdf"
              />
            </div>
          </div>
        </div>
        
        <div className="p-6 border-t flex justify-end gap-3 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            style={{ backgroundColor: COLORS.red, color: 'white' }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Matrix Manager - single image upload for values matrix
function MatrixManager({ matrixUrl, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [currentUrl, setCurrentUrl] = useState(matrixUrl);
  const [saving, setSaving] = useState(false);
  
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await api.post('/voting-guide/manage/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCurrentUrl(res.data.url);
      // Auto-save when uploaded
      await api.put('/voting-guide/manage/matrix', { matrix_url: res.data.url });
      toast.success('Matrix image uploaded and saved');
      onUpdate();
    } catch (e) {
      console.error(e);
      toast.error('Failed to upload image');
    }
    setUploading(false);
  };
  
  const handleRemove = async () => {
    setSaving(true);
    try {
      await api.put('/voting-guide/manage/matrix', { matrix_url: null });
      setCurrentUrl(null);
      toast.success('Matrix image removed');
      onUpdate();
    } catch (e) {
      toast.error('Failed to remove image');
    }
    setSaving(false);
  };
  
  return (
    <div className="bg-white rounded-xl shadow p-6">
      <h2 className="text-lg font-bold mb-2" style={{ color: COLORS.navy }}>Values Matrix</h2>
      <p className="text-sm text-gray-500 mb-6">
        Upload a single image that will be shown to all users when they click "View Values Matrix". 
        This replaces the previous party-based system.
      </p>
      
      {currentUrl ? (
        <div className="space-y-4">
          {/* Preview */}
          <div className="border rounded-lg overflow-hidden">
            <img 
              src={currentUrl} 
              alt="Values Matrix Preview" 
              className="w-full max-h-96 object-contain bg-gray-50"
            />
          </div>
          
          {/* Actions */}
          <div className="flex gap-3">
            <label className="flex-1">
              <input
                type="file"
                accept=".png,.jpg,.jpeg,.webp"
                onChange={handleUpload}
                disabled={uploading}
                className="hidden"
              />
              <Button
                variant="outline"
                className="w-full"
                disabled={uploading}
                onClick={() => document.querySelector('input[type="file"]').click()}
              >
                {uploading ? 'Uploading...' : 'Replace Image'}
              </Button>
            </label>
            <Button
              onClick={handleRemove}
              disabled={saving}
              variant="outline"
              className="text-red-600 border-red-300 hover:bg-red-50"
            >
              {saving ? 'Removing...' : 'Remove'}
            </Button>
          </div>
          
          {/* URL Display */}
          <div className="text-xs text-gray-400 truncate">
            URL: {currentUrl}
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <div className="text-gray-400 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="text-gray-500 mb-4">No matrix image uploaded yet</p>
          <label>
            <input
              type="file"
              accept=".png,.jpg,.jpeg,.webp"
              onChange={handleUpload}
              disabled={uploading}
              className="hidden"
            />
            <Button
              style={{ backgroundColor: COLORS.red, color: 'white' }}
              disabled={uploading}
              onClick={(e) => {
                e.preventDefault();
                e.target.closest('label').querySelector('input').click();
              }}
            >
              {uploading ? 'Uploading...' : 'Upload Matrix Image'}
            </Button>
          </label>
        </div>
      )}
    </div>
  );
}

// Analytics Dashboard
function AnalyticsDashboard({ analytics }) {
  if (!analytics) return null;
  
  return (
    <div className="bg-white rounded-xl shadow p-6">
      <h2 className="text-lg font-bold mb-4" style={{ color: COLORS.navy }}>Analytics</h2>
      
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-blue-600">{analytics.total_views || 0}</div>
          <div className="text-sm text-gray-600">Total Views</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-green-600">{analytics.total_htv_downloads || 0}</div>
          <div className="text-sm text-gray-600">HTV Downloads</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-purple-600">{analytics.total_report_downloads || 0}</div>
          <div className="text-sm text-gray-600">Report Downloads</div>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-4">
        <div>
          <h3 className="font-medium mb-2 text-sm">Views by Electorate</h3>
          <div className="max-h-40 overflow-y-auto space-y-1">
            {(analytics.views_by_electorate || []).slice(0, 10).map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="truncate">{item.electorate}</span>
                <span className="font-medium">{item.views}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="font-medium mb-2 text-sm">HTV Downloads by Electorate</h3>
          <div className="max-h-40 overflow-y-auto space-y-1">
            {(analytics.htv_by_electorate || []).slice(0, 10).map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="truncate">{item.electorate}</span>
                <span className="font-medium">{item.downloads}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="font-medium mb-2 text-sm">Report Downloads by Electorate</h3>
          <div className="max-h-40 overflow-y-auto space-y-1">
            {(analytics.report_by_electorate || []).slice(0, 10).map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="truncate">{item.electorate}</span>
                <span className="font-medium">{item.downloads}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Main Component
export default function VotingGuideManage() {
  const navigate = useNavigate();
  const [electorates, setElectorates] = useState([]);
  const [matrixUrl, setMatrixUrl] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingElectorate, setEditingElectorate] = useState(null);
  const [filter, setFilter] = useState('all'); // all, complete, incomplete
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('electorates'); // electorates, matrix, analytics
  
  const fetchData = useCallback(async () => {
    try {
      const [elecRes, matrixRes, analyticsRes] = await Promise.all([
        api.get('/voting-guide/manage/electorates'),
        api.get('/voting-guide/manage/matrix'),
        api.get('/voting-guide/manage/analytics')
      ]);
      setElectorates(elecRes.data.electorates || []);
      setMatrixUrl(matrixRes.data.matrix_url || null);
      setAnalytics(analyticsRes.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Access restricted to Turning Point Australia');
        navigate('/campaigns');
      } else {
        toast.error('Failed to load data');
      }
    }
    setLoading(false);
  }, [navigate]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const filteredElectorates = electorates.filter(e => {
    const matchesFilter = 
      filter === 'all' ||
      (filter === 'complete' && e.is_complete) ||
      (filter === 'incomplete' && !e.is_complete);
    
    const matchesSearch = 
      !search ||
      e.electorate_name.toLowerCase().includes(search.toLowerCase()) ||
      (e.base_mp_name || '').toLowerCase().includes(search.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-gray-300 border-t-red-500 rounded-full animate-spin"></div>
      </div>
    );
  }
  
  const completeCount = electorates.filter(e => e.is_complete).length;
  
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_c1b1098d-9b0b-48d8-baf0-b2eee2fcd660/artifacts/9fvy59b8_zw02imxt_TPAUS%20-%20Logo.png" 
                alt="TPAUS" 
                className="h-10 object-contain"
              />
              <div>
                <h1 className="text-xl font-semibold" style={{ color: COLORS.navy, fontFamily: "'Playfair Display', serif" }}>Voting Guide Manager</h1>
                <p className="text-sm text-gray-500">SA State Election</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => window.open('/voting-guide', '_blank')}
              >
                Preview Widget
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/campaigns')}
              >
                Back to Campaigns
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Stats Bar */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-6">
          <div className="text-sm">
            <span className="text-gray-500">Electorates:</span>
            <span className="font-bold ml-1">{electorates.length}</span>
          </div>
          <div className="text-sm">
            <span className="text-gray-500">Complete:</span>
            <span className="font-bold ml-1 text-green-600">{completeCount}</span>
          </div>
          <div className="text-sm">
            <span className="text-gray-500">Incomplete:</span>
            <span className="font-bold ml-1 text-orange-600">{electorates.length - completeCount}</span>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {['electorates', 'matrix', 'analytics'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === tab 
                  ? 'text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              style={activeTab === tab ? { backgroundColor: COLORS.navy } : {}}
            >
              {tab === 'matrix' ? 'Matrix' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
        
        {/* Electorates Tab */}
        {activeTab === 'electorates' && (
          <>
            {/* Filters */}
            <div className="bg-white rounded-xl shadow p-4 mb-6 flex items-center gap-4">
              <Input
                placeholder="Search electorates or MPs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-xs"
              />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">All Electorates</option>
                <option value="complete">Complete Only</option>
                <option value="incomplete">Incomplete Only</option>
              </select>
            </div>
            
            {/* Electorates Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredElectorates.map((elec, idx) => (
                <div 
                  key={idx}
                  className={`bg-white rounded-xl shadow p-4 border-l-4 ${
                    elec.is_complete ? 'border-l-green-500' : 'border-l-orange-500'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold" style={{ color: COLORS.navy }}>{elec.electorate_name}</h3>
                      <p className="text-sm text-gray-500">
                        {elec.custom_mp_name || elec.base_mp_name || 'Unknown MP'}
                      </p>
                      <p className="text-xs text-gray-400">
                        {elec.custom_mp_party || elec.base_mp_party || 'Unknown Party'}
                      </p>
                    </div>
                    {elec.star_rating && (
                      <div className="flex items-center gap-1 text-sm">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill={COLORS.red}>
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                        {elec.star_rating}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 text-xs mb-3">
                    <span className={`px-2 py-0.5 rounded ${elec.voting_report_png_url ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      Report
                    </span>
                    <span className={`px-2 py-0.5 rounded ${elec.htv_card_png_url ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      HTV
                    </span>
                    <span className={`px-2 py-0.5 rounded ${elec.mp_photo_url ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      Photo
                    </span>
                  </div>
                  
                  <Button
                    onClick={() => setEditingElectorate(elec)}
                    className="w-full"
                    variant="outline"
                    size="sm"
                  >
                    Edit
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}
        
        {/* Matrix Tab */}
        {activeTab === 'matrix' && (
          <MatrixManager matrixUrl={matrixUrl} onUpdate={fetchData} />
        )}
        
        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <AnalyticsDashboard analytics={analytics} />
        )}
      </div>
      
      {/* Edit Modal */}
      {editingElectorate && (
        <ElectorateEditModal
          electorate={editingElectorate}
          onClose={() => setEditingElectorate(null)}
          onSave={fetchData}
        />
      )}
    </div>
  );
}
